import React, { useState } from 'react';

import { useQuery } from '@apollo/client/react';
import { c } from 'ttag';

import {
    Badge,
    Button,
    RegionState,
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableHeaderCellProps,
    TableRow,
} from '@freelancelabs/components';

import Card from '../../components/Card';
import { OrganizationKind } from '../../graphql/__generated__/schema';
import { AssignableDashboardsDocument } from '../../graphql/__generated__/types';
import { refusalCode } from '../../graphql/refusalCode';
import DashboardDialog, { DashboardDialogMode } from './DashboardDialog';
import DeleteDashboardDialog from './DeleteDashboardDialog';

interface Props {
    accountId: string;
    accountName: string;
    kind: OrganizationKind | null | undefined;
}

type Editing = { mode: DashboardDialogMode; entry?: { id: string; name: string } | null };

// Read through assignableDashboards, which returns live entries only and is not an admin view:
// it can list, declare, rename and delete, but not show an archived entry or edit anything but the label.
const DashboardCataloguePanel = ({ accountId, accountName, kind }: Props) => {
    const [editing, setEditing] = useState<Editing | null>(null);
    const [deleting, setDeleting] = useState<{ id: string; name: string } | null>(null);

    // Skipped on Freelance.com: the internal organisation carries no client dashboard.
    const internal = kind === OrganizationKind.Internal;
    const { data, loading, error } = useQuery(AssignableDashboardsDocument, {
        variables: { organizationIds: [accountId] },
        skip: internal,
    });

    if (internal) {
        return null;
    }

    const dashboards = (data?.assignableDashboards?.dashboards ?? []).filter(Boolean);

    const columns: TableHeaderCellProps[] = [
        { id: 'name', label: c('Dashboard catalogue').t`Label read by the client`, minWidth: 240 },
        { id: 'metabase', label: c('Dashboard catalogue').t`Metabase id`, minWidth: 110 },
        { id: 'perimeter', label: c('Dashboard catalogue').t`Perimeter`, minWidth: 200 },
        { id: 'actions', label: '', minWidth: 190, textAlign: 'end' },
    ];

    // The read is bounded by manage_users, where declaring takes manage_accounts: the two can differ.
    const readRefusal = () => {
        switch (refusalCode(error)) {
            case 'MISSING_PERMISSION':
                return c('Dashboard catalogue')
                    .t`Your role does not let you manage users, and this catalogue is read under that permission. It was refused, not answered: it is not empty, it was not read.`;
            case 'FORBIDDEN_OUT_OF_SCOPE':
                return c('Dashboard catalogue')
                    .t`This account is outside your portfolio, so its catalogue was refused rather than answered.`;
            default:
                return c('Dashboard catalogue')
                    .t`The catalogue of this account could not be read, so what it holds is unknown — this is not the same as holding nothing.`;
        }
    };

    return (
        <>
            <Card
                title={c('Dashboard catalogue').t`Dashboards of this account`}
                counter={error ? undefined : dashboards.length}
                actions={
                    <Button category="weak" onClick={() => setEditing({ mode: 'declare' })}>
                        {c('Dashboard catalogue').t`Declare a dashboard…`}
                    </Button>
                }
            >
                <Table>
                    <TableHeader resizable cells={columns} />
                    <TableBody loading={loading} loadingRows={2} loadingColumns={columns.length}>
                        {dashboards.map((dashboard) => {
                            const id = dashboard.id as string;
                            const name = dashboard.name ?? '';
                            // False means no locked parameter, so the token maker would sign nothing;
                            // shown rather than filtered so the catalogue does not look emptier than it is.
                            const locked = dashboard.perimeterLocked === true;
                            return (
                                <TableRow key={id}>
                                    <TableCell>
                                        <span className="font-medium">{name}</span>
                                    </TableCell>
                                    <TableCell>
                                        <span className="font-mono text-sm text-text-700">
                                            {dashboard.metabaseDashboardId}
                                        </span>
                                    </TableCell>
                                    <TableCell>
                                        {locked ? (
                                            <Badge category="success" size="small">
                                                {c('Dashboard catalogue').t`Locked to this account`}
                                            </Badge>
                                        ) : (
                                            <span className="flex flex-col gap-1">
                                                <Badge category="warning" size="small">
                                                    {c('Dashboard catalogue').t`No perimeter parameter`}
                                                </Badge>
                                                <span className="text-xs text-warning-600">
                                                    {c('Dashboard catalogue')
                                                        .t`Cannot be granted: it would be signed for nobody and open nothing. Delete it and declare it again with its parameter.`}
                                                </span>
                                            </span>
                                        )}
                                    </TableCell>
                                    <TableCell className="text-end">
                                        <span className="inline-flex gap-2">
                                            <Button
                                                category="weak"
                                                size="small"
                                                onClick={() => setEditing({ mode: 'rename', entry: { id, name } })}
                                            >
                                                {c('Action').t`Rename`}
                                            </Button>
                                            <Button
                                                category="weak"
                                                size="small"
                                                onClick={() => setDeleting({ id, name })}
                                            >
                                                {c('Action').t`Delete`}
                                            </Button>
                                        </span>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>

                {error && (
                    <RegionState
                        tone="error"
                        title={c('Dashboard catalogue').t`This catalogue could not be read.`}
                        description={readRefusal()}
                    />
                )}
                {!loading && !error && dashboards.length === 0 && (
                    <RegionState
                        title={c('Dashboard catalogue').t`No dashboard is declared on this account.`}
                        description={c('Dashboard catalogue')
                            .t`That is the ordinary state of a new account. Declare an entry to be able to grant it.`}
                    />
                )}
            </Card>

            {editing && (
                <DashboardDialog
                    mode={editing.mode}
                    accountId={accountId}
                    accountName={accountName}
                    entry={editing.entry}
                    onClose={() => setEditing(null)}
                />
            )}
            {deleting && (
                <DeleteDashboardDialog entry={deleting} accountName={accountName} onClose={() => setDeleting(null)} />
            )}
        </>
    );
};

export default DashboardCataloguePanel;
